import React from 'react';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BarChartIcon from '@mui/icons-material/BarChart';
import BoltIcon from '@mui/icons-material/Bolt';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import CampaignIcon from '@mui/icons-material/Campaign';
import CelebrationIcon from '@mui/icons-material/Celebration';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ComputerIcon from '@mui/icons-material/Computer';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExtensionIcon from '@mui/icons-material/Extension';
import FaceIcon from '@mui/icons-material/Face';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import GridViewIcon from '@mui/icons-material/GridView';
import GroupsIcon from '@mui/icons-material/Groups';
import HandshakeIcon from '@mui/icons-material/Handshake';
import HomeIcon from '@mui/icons-material/Home';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import HubIcon from '@mui/icons-material/Hub';
import InboxIcon from '@mui/icons-material/Inbox';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LockIcon from '@mui/icons-material/Lock';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import MenuIcon from '@mui/icons-material/Menu';
import MicIcon from '@mui/icons-material/Mic';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PaletteIcon from '@mui/icons-material/Palette';
import PetsIcon from '@mui/icons-material/Pets';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import PsychologyIcon from '@mui/icons-material/Psychology';
import PublicIcon from '@mui/icons-material/Public';
import QuizIcon from '@mui/icons-material/Quiz';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SearchIcon from '@mui/icons-material/Search';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';
import SettingsIcon from '@mui/icons-material/Settings';
import SpaIcon from '@mui/icons-material/Spa';
import SportsBarIcon from '@mui/icons-material/SportsBar';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TvIcon from '@mui/icons-material/Tv';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import WavingHandIcon from '@mui/icons-material/WavingHand';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import ZoomInIcon from '@mui/icons-material/ZoomIn';

/** Named icons used across the app — prefer these over raw emojis in UI chrome. */
export type AppIconName =
  | 'arrowBack'
  | 'assignment'
  | 'barChart'
  | 'bolt'
  | 'businessCenter'
  | 'campaign'
  | 'celebration'
  | 'check'
  | 'close'
  | 'compareArrows'
  | 'computer'
  | 'directionsRun'
  | 'expandMore'
  | 'extension'
  | 'face'
  | 'familyRestroom'
  | 'favorite'
  | 'fullscreen'
  | 'gamepad'
  | 'gridView'
  | 'handshake'
  | 'home'
  | 'hourglass'
  | 'howToVote'
  | 'hub'
  | 'inbox'
  | 'info'
  | 'lightbulb'
  | 'localBar'
  | 'lock'
  | 'medal'
  | 'menu'
  | 'menuBook'
  | 'mic'
  | 'musicNote'
  | 'openInNew'
  | 'palette'
  | 'people'
  | 'pets'
  | 'photoCamera'
  | 'premium'
  | 'psychology'
  | 'public'
  | 'quiz'
  | 'refresh'
  | 'schedule'
  | 'search'
  | 'sentimentSatisfied'
  | 'sentimentVerySatisfied'
  | 'settings'
  | 'spa'
  | 'sportsBar'
  | 'tag'
  | 'thumbDown'
  | 'thumbUp'
  | 'trackChanges'
  | 'travelExplore'
  | 'trendingUp'
  | 'trophy'
  | 'tv'
  | 'visibility'
  | 'volumeUp'
  | 'warning'
  | 'wavingHand'
  | 'whatshot'
  | 'zoomIn';

const ICON_MAP = {
  arrowBack: ArrowBackIcon,
  assignment: AssignmentIcon,
  barChart: BarChartIcon,
  bolt: BoltIcon,
  businessCenter: BusinessCenterIcon,
  campaign: CampaignIcon,
  celebration: CelebrationIcon,
  check: CheckIcon,
  close: CloseIcon,
  compareArrows: CompareArrowsIcon,
  computer: ComputerIcon,
  directionsRun: DirectionsRunIcon,
  expandMore: ExpandMoreIcon,
  extension: ExtensionIcon,
  face: FaceIcon,
  familyRestroom: FamilyRestroomIcon,
  favorite: FavoriteIcon,
  fullscreen: FullscreenIcon,
  gamepad: SportsEsportsIcon,
  gridView: GridViewIcon,
  handshake: HandshakeIcon,
  home: HomeIcon,
  hourglass: HourglassEmptyIcon,
  howToVote: HowToVoteIcon,
  hub: HubIcon,
  inbox: InboxIcon,
  info: InfoOutlinedIcon,
  lightbulb: LightbulbIcon,
  localBar: LocalBarIcon,
  lock: LockIcon,
  medal: MilitaryTechIcon,
  menu: MenuIcon,
  menuBook: MenuBookIcon,
  mic: MicIcon,
  musicNote: MusicNoteIcon,
  openInNew: OpenInNewIcon,
  palette: PaletteIcon,
  people: GroupsIcon,
  pets: PetsIcon,
  photoCamera: PhotoCameraIcon,
  premium: WorkspacePremiumIcon,
  psychology: PsychologyIcon,
  public: PublicIcon,
  quiz: QuizIcon,
  refresh: RefreshIcon,
  schedule: ScheduleIcon,
  search: SearchIcon,
  sentimentSatisfied: SentimentSatisfiedAltIcon,
  sentimentVerySatisfied: SentimentVerySatisfiedIcon,
  settings: SettingsIcon,
  spa: SpaIcon,
  sportsBar: SportsBarIcon,
  tag: LocalOfferIcon,
  thumbDown: ThumbDownIcon,
  thumbUp: ThumbUpIcon,
  trackChanges: TrackChangesIcon,
  travelExplore: TravelExploreIcon,
  trendingUp: TrendingUpIcon,
  trophy: EmojiEventsIcon,
  tv: TvIcon,
  visibility: VisibilityIcon,
  volumeUp: VolumeUpIcon,
  warning: WarningAmberIcon,
  wavingHand: WavingHandIcon,
  whatshot: WhatshotIcon,
  zoomIn: ZoomInIcon,
} as const;

export interface AppIconProps extends Omit<SvgIconProps, 'color'> {
  name: AppIconName;
}

export const AppIcon: React.FC<AppIconProps> = ({
  name,
  className,
  fontSize = 'inherit',
  ...props
}) => {
  const Icon = ICON_MAP[name];
  return (
    <Icon
      className={['app-icon', className].filter(Boolean).join(' ')}
      fontSize={fontSize}
      {...props}
    />
  );
};
